import { Command } from 'commander';

import { clearApplicationSession } from '../lib/application-session.ts';
import { clearCredentials } from '../lib/auth.ts';
import { loadConfig, resetConfig, saveConfig } from '../lib/config.ts';
import { isLocalDev } from '../lib/env.ts';
import { error, hint, info, isJsonMode, printJson, success } from '../lib/output.ts';

// The token endpoint is a different host from the federation endpoint in every
// environment, so it cannot be derived — switching environments has to move both
// or an application credential keeps being exchanged against the previous one.
export const ENVIRONMENTS = [
  {
    name: 'production',
    label: 'federation.sortlist.com',
    endpoint: 'https://federation.sortlist.com/graphql',
    tokenEndpoint: 'https://hermes.production.sortlist.cloud/oauth/token',
  },
  {
    name: 'staging',
    label: 'federation.sortlist-test.com',
    endpoint: 'https://federation.sortlist-test.com/graphql',
    tokenEndpoint: 'https://hermes.staging.sortlist.cloud/oauth/token',
  },
  {
    name: 'local',
    label: 'localhost:4020',
    endpoint: 'http://localhost:4020/graphql',
    tokenEndpoint: 'http://localhost:8003/oauth/token',
  },
] as const;

export const configCommand = new Command('config')
  .summary('View or update CLI configuration')
  .description(
    'View or update CLI configuration\n\n' +
      'Manage your company context and credentials.\n\n' +
      'Examples:\n' +
      '  sl config show              See current endpoint and company\n' +
      '  sl config set company <slug|id>  Act for that company on every command\n' +
      (isLocalDev ? '  sl config set endpoint <url>  Point to a custom endpoint\n' : '') +
      '  sl config reset             Clear all config and credentials',
  );

configCommand
  .command('show')
  .description('Show current configuration')
  .action(function (this: Command) {
    const opts = this.optsWithGlobals();
    const config = loadConfig();
    if (isJsonMode(opts)) {
      printJson(config);
    } else {
      info(`Endpoint:       ${config.endpoint}`);
      if (config.tokenEndpoint) info(`Token endpoint: ${config.tokenEndpoint}`);
      if (config.company) info(`Company:        ${config.company}`);
    }
  });

// `endpoint` is dev-only — published builds always target production, so the
// endpoint must not be overridable there (mirrors the gated use-* commands).
const SETTABLE_KEYS = [...(isLocalDev ? ['endpoint'] : []), 'company', 'tokenEndpoint'];

configCommand
  .command('set <key> <value>')
  .description(`Set a config value (${SETTABLE_KEYS.join(', ')})`)
  .action((key: string, value: string) => {
    if (!SETTABLE_KEYS.includes(key)) {
      error(`Unknown config key: "${key}".`);
      info('');
      info('  Allowed keys:');
      for (const k of SETTABLE_KEYS) {
        info(`    ${k}`);
      }
      hint('Usage: sl config set <key> <value>');
      process.exit(1);
    }
    saveConfig({ [key]: value });
    success(`Set ${key} = ${value}`);
  });

if (isLocalDev) {
  for (const environment of ENVIRONMENTS) {
    configCommand
      .command(`use-${environment.name}`)
      .description(`Point CLI to ${environment.name} (${environment.label})`)
      .action(() => {
        saveConfig({ endpoint: environment.endpoint, tokenEndpoint: environment.tokenEndpoint });
        success(`Endpoint set to ${environment.endpoint}`);
        info(`Token endpoint set to ${environment.tokenEndpoint}`);
        hint(`Run \`sl login\` to authenticate against ${environment.name}.`);
      });
  }
}

configCommand
  .command('reset')
  .description('Reset all config and credentials')
  .action(() => {
    resetConfig();
    clearCredentials();
    clearApplicationSession();
    success('Config, credentials and any application session cleared.');
    hint('Run `sl login` to re-authenticate.');
  });
